import { Box, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Circle, Minus, Plus } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { RuleGraphConditionSubject } from "@intelligems/ig-types";

export const RULE_GRAPH_VIEWPORT_CONTROL_HEIGHT = 30;
export const RULE_GRAPH_VIEWPORT_CONTROL_INSET = 12;

type RuleGraphLaneDotState = "default" | "filled" | "pending";

type RuleGraphLaneLabelType = "when" | "fallback" | "fallback-when";

/**
 * The row-header sentence per condition subject. `when` and `fallbackWhen`
 * lead into the condition chip; `whenAnything` finishes the sentence itself
 * for a row with no chip. Only the cart's `whenAnything` is a real state (an
 * always-matching branch, or the input heading a branchless rule); the
 * others are a defensive floor, since the prompts forbid a non-cart subject
 * on a branch with nothing to chip.
 */
export const RULE_GRAPH_CONDITION_PHRASES: Record<
  RuleGraphConditionSubject,
  { when: string; whenAnything: string; fallbackWhen: string }
> = {
  cart: {
    when: "When cart contains",
    whenAnything: "When cart contains anything",
    fallbackWhen: "If nothing matches and cart contains",
  },
  delivery: {
    when: "When delivery is",
    whenAnything: "For any delivery option",
    fallbackWhen: "If nothing matches and delivery is",
  },
  shipping: {
    when: "When shipping is",
    whenAnything: "For any shipping state",
    fallbackWhen: "If nothing matches and shipping is",
  },
  customer: {
    when: "When the shopper has",
    whenAnything: "For any shopper",
    fallbackWhen: "If nothing matches and the shopper has",
  },
  page: {
    when: "When the page is",
    whenAnything: "On any page",
    fallbackWhen: "If nothing matches and the page is",
  },
  order: {
    when: "When the order has",
    whenAnything: "For any order",
    fallbackWhen: "If nothing matches and the order has",
  },
  time: {
    when: "When the time is",
    whenAnything: "At any time",
    fallbackWhen: "If nothing matches and the time is",
  },
};

const laneLabelPhrase = (
  type: RuleGraphLaneLabelType,
  hasCondition: boolean,
  subject: RuleGraphConditionSubject
): string => {
  if (type === "fallback") return "If nothing matches then";
  const phrases = RULE_GRAPH_CONDITION_PHRASES[subject];
  if (type === "fallback-when") return phrases.fallbackWhen;
  return hasCondition ? phrases.when : phrases.whenAnything;
};

export const RuleGraphLaneDot = ({
  state = "default",
}: {
  state?: RuleGraphLaneDotState;
}) => (
  <Box
    aria-hidden="true"
    data-state={state}
    sx={(theme) => ({
      backgroundColor:
        state === "filled"
          ? theme.palette.text.interactive.neutral.default
          : theme.igBackgroundColors.baseLightLow,
      border: `2px solid ${
        state === "filled"
          ? theme.palette.text.interactive.neutral.default
          : theme.igBackgroundColors.interactive.dark.disabled
      }`,

      borderRadius: "50%",
      boxSizing: "border-box",
      // The dot is a flex child of a row that can hold wide condition chips;
      // without this it is squeezed into an ellipse.
      flexShrink: 0,
      height: 9,
      opacity: state === "pending" ? 0.35 : 1,
      width: 9,
    })}
  />
);

export const RuleGraphLaneLabel = ({
  type = "when",
  state = "default",
  color,
  hasCondition = false,
  subject = "cart",
  children,
}: {
  /**
   * "when": an entry row, gated by its own condition. "fallback": a row that
   * runs because the one above came up empty. "fallback-when": a fallback row
   * that ALSO carries a condition of its own, so the header composes both —
   * "If nothing matches and cart contains <chip>" — instead of hanging a
   * condition chip off "then", which reads as the fallback's output.
   */
  type?: RuleGraphLaneLabelType;
  /**
   * Which request context the chip is about; picks the sentence around it
   * ("When cart contains" vs "When delivery is"). Defaults to the cart, the
   * subject every graph spoke before `meta.conditionSubject` existed.
   */
  subject?: RuleGraphConditionSubject;
  state?: RuleGraphLaneDotState;
  /** The row's text color, so a ghosted lane fades its sentence too. */
  color?: string;
  /**
   * Whether a condition chip (or its skeleton stand-in) follows the phrase.
   * An always-matching row has no targeting value to chip, and "When cart
   * contains" alone dangles as an unfinished sentence — so the phrase
   * absorbs the missing chip and reads "When cart contains anything". The
   * fallback phrases are unaffected: "If nothing matches then" already ends
   * on a complete thought, and "…and cart contains" is only ever chosen for a
   * row that HAS a condition. Defaults to false so a caller that promises no
   * chip can never leave the sentence hanging.
   */
  hasCondition?: boolean;
  /** Rendered inline after the bold header text (the rule's own label). */
  children?: ReactNode;
}) => {
  const phrase = laneLabelPhrase(type, hasCondition, subject);

  return (
    <Box sx={{ alignItems: "center", display: "flex", gap: 2, minWidth: 0 }}>
      <RuleGraphLaneDot state={state} />
      <Typography
        sx={{
          color,
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.5,
          whiteSpace: "nowrap",
        }}
      >
        {phrase}
      </Typography>
      {children}
    </Box>
  );
};

export const RuleGraphCartShareBadge = ({
  percentage,
}: {
  percentage: number;
}) => (
  <Box
    sx={(theme) => ({
      alignItems: "center",
      backgroundColor: theme.igPrimitives.gray[500],
      borderRadius: 0.75,
      boxSizing: "border-box",
      display: "flex",
      gap: 0.5,
      height: 26,
      px: 2.5,
      py: 1,
      whiteSpace: "nowrap",
      width: 87,
    })}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.5 }}>
      {percentage}%
    </Typography>
    <Typography
      sx={(theme) => ({
        color: theme.palette.text.tertiary,
        fontSize: 10,
        lineHeight: 1.5,
      })}
    >
      of carts
    </Typography>
  </Box>
);

/**
 * The shared canvas-pill surface every floating viewport control sits on:
 * white card, hairline border, low shadow. Exported so controls that live in
 * their own file (e.g. the regenerate button) stay pixel-identical.
 */
export const ruleGraphCanvasControlSx =
  (disabled: boolean) => (theme: Theme) => ({
    alignItems: "center",
    backgroundColor: theme.igBackgroundColors.baseLightHigh,
    border: `1px solid ${theme.igBorders.core.default}`,
    // Figma `border radius/lg`; the pill's hairline border is inside the
    // Figma-specified 113x30 box, so it must not grow the control.
    borderRadius: "8px",
    boxShadow: theme.igShadows.low,
    boxSizing: "border-box",
    color: theme.palette.text.primary,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    font: "inherit",
    justifyContent: "center",
    opacity: disabled ? 0.5 : 1,
    padding: 0,
    transition: "background-color 120ms ease, border-color 120ms ease",
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.text.interactive.neutral.default}`,
      outlineOffset: 2,
    },
  });

const zoomButtonSx = (disabled: boolean) => (theme: Theme) => ({
  alignItems: "center",
  backgroundColor: "transparent",
  border: 0,
  borderRadius: "6px",
  cursor: disabled ? "not-allowed" : "pointer",
  display: "flex",
  height: 22,
  justifyContent: "center",
  padding: 0,
  width: 22,
  "&:hover:not(:disabled)": {
    backgroundColor: theme.igBackgroundColors.interactive.neutral.default,
  },
  "&:active:not(:disabled)": {
    backgroundColor: theme.igBackgroundColors.interactive.neutral.active,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.text.interactive.neutral.default}`,
    outlineOffset: 1,
  },
});

export const RuleGraphZoomControl = ({
  zoomPercentage,
  onZoomIn,
  onZoomOut,
  zoomInDisabled = false,
  zoomOutDisabled = false,
}: {
  zoomPercentage: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  zoomInDisabled?: boolean;
  zoomOutDisabled?: boolean;
}) => {
  const [isZoomOutHovered, setIsZoomOutHovered] = useState(false);
  const [isZoomOutPressed, setIsZoomOutPressed] = useState(false);
  const showActiveZoomOutIcon =
    !zoomOutDisabled && (isZoomOutHovered || isZoomOutPressed);

  return (
    <Box
      component="div"
      sx={(theme) => ({
        alignItems: "center",
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "8px",
        boxShadow: theme.igShadows.low,
        boxSizing: "border-box",
        display: "flex",
        gap: 0.5,
        height: RULE_GRAPH_VIEWPORT_CONTROL_HEIGHT,
        justifyContent: "center",
        px: 1,
        py: 1,
        width: 82,
      })}
    >
      <Box
        aria-label="Zoom out"
        component="button"
        disabled={zoomOutDisabled}
        onClick={onZoomOut}
        onMouseEnter={() => setIsZoomOutHovered(true)}
        onMouseLeave={() => setIsZoomOutHovered(false)}
        onPointerCancel={() => setIsZoomOutPressed(false)}
        onPointerDown={() => setIsZoomOutPressed(true)}
        onPointerLeave={() => setIsZoomOutPressed(false)}
        onPointerUp={() => setIsZoomOutPressed(false)}
        sx={zoomButtonSx(zoomOutDisabled)}
        type="button"
      >
        <Box
          aria-hidden="true"
          component={Minus}
          data-testid="rule-graph-zoom-minus-icon"
          size={13}
          strokeWidth={1.33}
          sx={(theme) => ({
            color: showActiveZoomOutIcon
              ? theme.palette.text.primary
              : theme.palette.text.tertiary,
          })}
        />
      </Box>
      <Typography sx={{ fontSize: 10, lineHeight: 1.5 }}>
        {zoomPercentage}%
      </Typography>
      <Box
        aria-label="Zoom in"
        component="button"
        disabled={zoomInDisabled}
        onClick={onZoomIn}
        sx={zoomButtonSx(zoomInDisabled)}
        type="button"
      >
        <Box
          aria-hidden="true"
          component={Plus}
          data-testid="rule-graph-zoom-plus-icon"
          size={13}
          strokeWidth={1.5}
          sx={(theme) => ({ color: theme.palette.text.tertiary })}
        />
      </Box>
    </Box>
  );
};

export const RuleGraphBackToStart = ({
  onClick,
  disabled = false,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const showsHoverState = isHovered && !disabled;

  return (
    <Box
      aria-label="Back to start"
      component="button"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={(theme) => ({
        ...ruleGraphCanvasControlSx(disabled)(theme),
        gap: 1.5,
        height: 30,
        px: 3,
        py: 1.5,
        width: 113,
        "&:hover:not(:disabled)": {
          borderColor: theme.igBackgroundColors.interactive.dark.disabled,
        },
        "&:active:not(:disabled)": {
          backgroundColor: theme.igBackgroundColors.interactive.neutral.active,
        },
      })}
      type="button"
    >
      <Box
        aria-hidden="true"
        component={Circle}
        data-testid="rule-graph-back-to-start-icon"
        size={10}
        strokeWidth={1.5}
        sx={(theme) => ({
          color: showsHoverState
            ? theme.palette.text.primary
            : theme.palette.text.tertiary,
        })}
      />
      <Typography
        component="span"
        sx={(theme) => ({
          color: showsHoverState
            ? theme.palette.text.primary
            : theme.palette.text.tertiary,
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: "nowrap",
        })}
      >
        Back to start
      </Typography>
    </Box>
  );
};
